import React, { useEffect, useRef, useState } from "react";

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 30 }, (_, i) => currentYear + 5 - i);

const isYearMonth = (value) => /^\d{4}-\d{2}$/.test(value || "");

const parseValue = (value) => {
  if (!isYearMonth(value)) return null;
  const [year, month] = value.split("-").map(Number);
  return { year, month: month - 1 };
};

const styles = `
  .myp-wrapper {
    position: relative;
    display: inline-block;
    font-family: 'DM Sans', system-ui, sans-serif;
  }

  .myp-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 10px;
    border-radius: 4px;
    background: var(--myp-bg, #f1f5f9);
    color: var(--myp-text, #64748b);
    font-size: 12px;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.1s, color 0.1s;
  }
  .myp-trigger:hover { background: #e2e8f0; }
  .myp-trigger.active { background: #0369a1; color: #fff; }
  .myp-trigger:disabled { opacity: 0.5; cursor: not-allowed; }

  .myp-caret { font-size: 8px; transition: transform 0.2s ease; }
  .myp-caret.open { transform: rotate(180deg); }

  .myp-box {
    position: absolute;
    top: calc(100% + 5px);
    right: 0;
    z-index: 99999;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    width: 220px;
    box-shadow: 0 8px 24px -4px rgba(0,0,0,0.1), 0 2px 8px -2px rgba(0,0,0,0.06);
    overflow: hidden;
    animation: myp-in 0.16s cubic-bezier(0.34, 1.3, 0.64, 1) both;
  }

  @keyframes myp-in {
    from { opacity: 0; transform: translateY(-5px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .myp-scroll {
    max-height: 260px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #e2e8f0 transparent;
  }
  .myp-scroll::-webkit-scrollbar { width: 3px; }
  .myp-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 3px; }

  .myp-year-item { border-bottom: 1px solid #e2e8f0; }
  .myp-year-item:last-child { border-bottom: none; }

  .myp-year-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    font-size: 12px;
    font-family: 'DM Sans', system-ui, sans-serif;
    color: #0f172a;
    cursor: pointer;
    padding: 8px 14px;
    box-sizing: border-box;
    transition: background 0.1s;
  }
  .myp-year-row:hover { background: #f8fafc; }
  .myp-year-row.active { font-weight: 600; color: #ff8820; background: #fff4ea; }

  .myp-year-chevron {
    font-size: 9px;
    color: #94a3b8;
    transition: transform 0.18s cubic-bezier(0.34,1.2,0.64,1);
  }
  .myp-year-chevron.open { transform: rotate(90deg); color: #ff8820; }

  .myp-months {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2px;
    padding: 7px 10px 9px;
    background: #fdf8f3;
    border-top: 1px solid #e2e8f0;
    animation: myp-slide-down 0.16s cubic-bezier(0.34,1.2,0.64,1) both;
  }

  @keyframes myp-slide-down {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .myp-mon-btn {
    background: none;
    border: 1px solid transparent;
    font-size: 11px;
    font-family: 'DM Sans', system-ui, sans-serif;
    color: #0f172a;
    cursor: pointer;
    padding: 5px 2px;
    text-align: center;
    border-radius: 6px;
    transition: background 0.1s, color 0.1s, transform 0.1s;
  }
  .myp-mon-btn:hover { background: #fff4ea; color: #ff8820; transform: scale(1.07); }
  .myp-mon-btn.active {
    background: #ff8820;
    color: #fff;
    font-weight: 600;
    box-shadow: 0 2px 5px rgba(255,136,32,0.35);
  }

  .myp-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px 8px;
    border-top: 1px solid #e2e8f0;
  }
  .myp-footer-btn {
    background: none;
    border: none;
    color: #ff8820;
    font-size: 11.5px;
    font-weight: 500;
    cursor: pointer;
    padding: 3px 6px;
    border-radius: 5px;
    font-family: 'DM Sans', system-ui, sans-serif;
    transition: background 0.1s;
  }
  .myp-footer-btn:hover { background: #fff4ea; }
`;

export default function MonthYearPicker({
  value = "",
  onChange = () => null,
  disabled = false,
  className = "",
  placeholder = "Due Date: All",
}) {
  const [open, setOpen] = useState(false);
  const parsed = parseValue(value);
  const [expandedYear, setExpandedYear] = useState(parsed?.year ?? currentYear);
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (parsed) setExpandedYear(parsed.year);
  }, [value]);

  useEffect(() => {
    if (open && scrollRef.current) {
      const active = scrollRef.current.querySelector(".active");
      if (active) active.scrollIntoView({ block: "center" });
    }
  }, [open]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleSelect = (year, monthIndex) => {
    const monthStr = String(monthIndex + 1).padStart(2, "0");
    onChange(`${year}-${monthStr}`);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  const label = parsed
    ? `${MONTHS_FULL[parsed.month].slice(0, 3)} ${parsed.year}`
    : placeholder;

  return (
    <>
      <style>{styles}</style>
      <div ref={wrapperRef} className={`myp-wrapper ${className}`}>
        <button
          type="button"
          className={`myp-trigger${parsed ? " active" : ""}`}
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
        >
          <span>{label}</span>
          <span className={`myp-caret${open ? " open" : ""}`}>▼</span>
        </button>

        {open && (
          <div className="myp-box">
            <div className="myp-scroll" ref={scrollRef}>
              {YEARS.map((y) => (
                <div className="myp-year-item" key={y}>
                  <button
                    type="button"
                    className={`myp-year-row${expandedYear === y ? " active" : ""}`}
                    onClick={() => setExpandedYear((prev) => (prev === y ? null : y))}
                  >
                    <span>{y}</span>
                    <span className={`myp-year-chevron${expandedYear === y ? " open" : ""}`}>›</span>
                  </button>
                  {expandedYear === y && (
                    <div className="myp-months">
                      {MONTHS_SHORT.map((m, i) => (
                        <button
                          key={m}
                          type="button"
                          className={`myp-mon-btn${parsed?.year === y && parsed?.month === i ? " active" : ""}`}
                          onClick={() => handleSelect(y, i)}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="myp-footer">
              <button className="myp-footer-btn" onClick={handleClear} type="button">Clear</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
